/**
 * Sprint 207W — Templates Fired (Last 24h) tile.
 *
 * Shows the founder how many teens received a verbatim onboarding template
 * delivery in the past 24h (i.e. how often the LLM-bypass path actually
 * caught new subscribers), with a 24-hour sparkline and unique-teens count.
 *
 * Pure read-only. Hits GET /api/ava-templates/metrics/recent.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Users, Loader2, RefreshCcw } from 'lucide-react';
import { apiClient } from '../../api/client';

const TRIGGER_LABEL = {
  'ava_template:first_session': 'First session',
  first_session: 'First session',
  second_session: 'Second session',
  lapse_day_2: 'Day-2 lapse',
  post_challenge_complete: 'Post-challenge',
  on_demand: 'On demand',
};

const Sparkline = ({ buckets = [] }) => {
  const max = Math.max(1, ...buckets);
  const w = 220;
  const h = 36;
  const step = buckets.length > 1 ? w / (buckets.length - 1) : 0;
  const pts = buckets
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible" data-testid="templates-fired-sparkline">
      <polyline
        fill="none"
        stroke="#7c3aed"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
      />
      {buckets.map((v, i) => (
        <circle
          key={i}
          cx={i * step}
          cy={h - (v / max) * (h - 4) - 2}
          r={v > 0 ? 2.2 : 1.2}
          fill={v > 0 ? '#7c3aed' : '#d4d4d8'}
        />
      ))}
    </svg>
  );
};

const TemplatesFiredCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.request('/api/ava-templates/metrics/recent');
      setData(resp);
    } catch (e) {
      // Non-admin viewers (sub-admins or teens stumbling in) just get a
      // silent hide so the tile doesn't shout 403 at them.
      const msg = e?.message || 'Failed to load';
      if (msg.includes('admin_only') || msg.includes('403')) {
        setData('hidden');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Component self-hides for non-admins so AdminHome doesn't render a broken card.
  if (data === 'hidden') return null;

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm px-5 py-4 flex flex-wrap items-center gap-5"
      data-testid="templates-fired-card"
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-md flex items-center justify-center bg-purple-50">
          <Sparkles size={18} className="text-purple-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Templates Fired · Last 24h</h3>
          <p className="text-xs text-gray-500">Verbatim onboarding deliveries that bypassed the LLM</p>
        </div>
      </div>

      <div className="flex items-baseline gap-1.5" data-testid="templates-fired-total">
        <span className="text-3xl font-bold text-gray-900">
          {loading ? '—' : (data?.total_fired ?? 0)}
        </span>
        <span className="text-xs text-gray-500 font-medium">fired</span>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-600" data-testid="templates-fired-unique">
        <Users size={13} className="text-gray-400" />
        <span className="font-semibold text-gray-900">{loading ? '—' : (data?.unique_teens ?? 0)}</span>
        <span>unique teens</span>
      </div>

      <div className="flex-1 min-w-[220px]">
        {loading ? (
          <div className="h-9 flex items-center text-xs text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> loading…
          </div>
        ) : (
          <Sparkline buckets={data?.sparkline || []} />
        )}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="templates-fired-trigger-chips">
        {Object.entries(data?.by_trigger || {}).map(([k, v]) => (
          <span
            key={k}
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700"
            data-testid={`templates-fired-chip-${k}`}
          >
            {TRIGGER_LABEL[k] || k} · {v}
          </span>
        ))}
      </div>

      <button
        onClick={reload}
        className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600"
        title="Reload"
        data-testid="templates-fired-reload"
        disabled={loading}
      >
        <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} />
      </button>

      {error && <span className="text-xs text-red-600 w-full" data-testid="templates-fired-error">{error}</span>}
    </div>
  );
};

export default TemplatesFiredCard;
